import { Component, useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from '../api/axios';

const ErrorContext = createContext();

export const useErrorReporter = () => useContext(ErrorContext);

/* ─── Send error to backend ─── */
const sendErrorReport = async (errorData) => {
  try {
    await axios.post('/error-log', {
      error_message: ['TypeError', 'ReferenceError', 'RangeError', 'SyntaxError'].find(
        name => String(errorData.message || '').startsWith(name)
      ) || 'FrontendError',

    });
  } catch (e) {
    console.error('Error boundary:', e);
    // Silently fail — don't cause infinite loop
  }
};

/* ─── Error Dialog (system dialog style) ─── */
const ErrorDialog = ({ error, onDismiss, isLight }) => {
  if (!error) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="error-dialog-overlay">
      <div
        className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${isLight ? 'bg-white' : 'bg-gray-900 border border-gray-700'}`}
        data-testid="error-dialog"
      >
        {/* Header */}
        <div className={`px-5 py-3 flex items-center gap-3 ${isLight ? 'bg-red-50 border-b border-red-100' : 'bg-red-900/30 border-b border-red-800/40'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-red-100' : 'bg-red-900/50'}`}>
            <svg className={`w-4 h-4 ${isLight ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className={`text-sm font-semibold ${isLight ? 'text-red-800' : 'text-red-300'}`} data-testid="error-dialog-title">
            Ein Fehler ist aufgetreten
          </h3>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className={`text-sm leading-relaxed ${isLight ? 'text-gray-600' : 'text-gray-400'}`}>
            Bitte versuche den Vorgang erneut. IO versucht, eine technische Fehlermeldung ohne Nachrichteninhalte zu protokollieren.
          </p>
          {error.message && (
            <div className={`mt-3 p-3 rounded-lg text-xs font-mono break-all max-h-24 overflow-y-auto ${isLight ? 'bg-gray-50 text-gray-500 border border-gray-200' : 'bg-gray-800 text-gray-500 border border-gray-700'}`} data-testid="error-dialog-details">
              {error.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-5 py-3 flex justify-end ${isLight ? 'bg-gray-50 border-t border-gray-100' : 'bg-gray-800/50 border-t border-gray-700/50'}`}>
          <button
            onClick={onDismiss}
            className={`auth-error-dismiss-btn px-5 py-2 rounded-lg text-sm font-medium transition-colors ${isLight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-black'}`}
            data-testid="error-dialog-dismiss"
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Error Provider (global handlers) ─── */
export const ErrorProvider = ({ children }) => {
  const [currentError, setCurrentError] = useState(null);
  const [isLight, setIsLight] = useState(false);

  // Watch theme changes
  useEffect(() => {
    const check = () => setIsLight(document.documentElement.classList.contains('light'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const reportError = useCallback((message, stack, component, extra) => {
    const errorData = { message, stack, component, extra };
    sendErrorReport(errorData);
    setCurrentError(errorData);
  }, []);

  // Global: unhandled JS errors
  useEffect(() => {
    const handleError = (event) => {
      // Ignore ResizeObserver and script loading errors
      if (event.message?.includes('ResizeObserver') || event.message?.includes('Script error')) return;
      reportError(
        event.message || 'Unbekannter JavaScript-Fehler',
        event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
        null,
        null
      );
    };

    const handleRejection = (event) => {
      const reason = event.reason;
      // Ignore cancelled requests and 401s
      if (reason?.response?.status === 401 || reason?.code === 'ERR_CANCELED') return;
      reportError(
        reason?.message || 'Unbehandelter Promise-Fehler',
        reason?.stack || null,
        null,
        null
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [reportError]);

  const dismiss = useCallback(() => setCurrentError(null), []);

  return (
    <ErrorContext.Provider value={{ reportError }}>
      {children}
      <ErrorDialog error={currentError} onDismiss={dismiss} isLight={isLight} />
    </ErrorContext.Provider>
  );
};

/* ─── React Error Boundary (catches render crashes) ─── */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    sendErrorReport({
      message: error?.message || 'React-Komponente abgestürzt',
      stack: error?.stack || null,
      component: errorInfo?.componentStack || null,
      extra: null,
    });
    // Auto-retry once after 500ms
    if (this.state.retryCount < 2) {
      setTimeout(() => {
        this.setState(prev => ({ hasError: false, error: null, retryCount: prev.retryCount + 1 }));
      }, 500);
    }
  }

  render() {
    if (this.state.hasError) {
      const isLight = document.documentElement.classList.contains('light');
      return (
        <div className={`min-h-screen flex items-center justify-center p-6 ${isLight ? 'bg-white' : 'bg-gray-900'}`}>
          <div className={`max-w-md w-full rounded-xl p-8 text-center ${isLight ? 'bg-white shadow-lg border border-gray-200' : 'bg-gray-800 border border-gray-700'}`} data-testid="error-boundary-fallback">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${isLight ? 'bg-red-50' : 'bg-red-900/30'}`}>
              <svg className={`w-7 h-7 ${isLight ? 'text-red-500' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className={`text-lg font-bold mb-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>
              Ein unerwarteter Fehler ist aufgetreten
            </h2>
            <p className={`text-sm mb-6 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
              Der Fehler wurde automatisch protokolliert. Bitte laden Sie die Seite neu.
            </p>
            <button
              onClick={() => window.location.reload()}
              className={`auth-error-dismiss-btn px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isLight ? 'bg-blue-600 hover:bg-blue-700 !text-white' : 'bg-yellow-400 hover:bg-yellow-500 text-black'}`}
              style={isLight ? { color: '#ffffff' } : undefined}
              data-testid="error-boundary-reload"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
